import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Save, Clock, User, MessageSquare } from 'lucide-react';
import { GiHorseHead } from 'react-icons/gi';
import { doc, updateDoc, arrayUnion, addDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';

const surveillanceColors = { 
  vert: "#22c55e", 
  jaune: "#facc15", 
  orange: "#fb923c", 
  rouge: "#ef4444" 
};

// Modal simple sans shadcn pour éviter les problèmes de z-index
const SimpleModal = ({ isOpen, onClose, children, title }) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 10000 }}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4">
          {children}
        </div>
      </div>
    </div>
  );
};

const ReportProblemModal = ({ isOpen, onClose, horse, onSuccess }) => {
  const [selectedState, setSelectedState] = useState('jaune');
  const [alertContent, setAlertContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [showStateSelector, setShowStateSelector] = useState(false);
  const [openTickets, setOpenTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [viewingTicket, setViewingTicket] = useState(null);

  // Réinitialiser à l'ouverture avec l'état actuel du cheval
  useEffect(() => {
    if (isOpen && horse) {
      setSelectedState(horse.SurveillanceEtat || 'jaune');
      setAlertContent('');
      setShowStateSelector(false);
      setViewingTicket(null);
      loadOpenTickets();
    }
  }, [isOpen, horse]);

  // Charger les tickets ouverts pour ce cheval
  const loadOpenTickets = async () => {
    if (!horse) return;
    
    setLoadingTickets(true);
    try {
      const q = query(
        collection(db, 'tickets'),
        where('horseId', '==', horse.id),
        where('statut', '==', 'ouvert'),
        orderBy('dateCreation', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const tickets = [];
      snapshot.forEach((doc) => {
        tickets.push({ id: doc.id, ...doc.data() });
      });
      
      setOpenTickets(tickets);
    } catch (error) {
      console.error('Erreur chargement tickets:', error);
      try {
        const q = query(
          collection(db, 'tickets'),
          where('horseId', '==', horse.id),
          where('statut', '==', 'ouvert')
        );
        
        const snapshot = await getDocs(q);
        const tickets = [];
        snapshot.forEach((doc) => {
          tickets.push({ id: doc.id, ...doc.data() });
        });
        
        tickets.sort((a, b) => {
          const dateA = new Date(a.dateCreation || 0);
          const dateB = new Date(b.dateCreation || 0);
          return dateB - dateA;
        });
        
        setOpenTickets(tickets);
      } catch (fallbackError) {
        console.error('Erreur fallback chargement tickets:', fallbackError);
        setOpenTickets([]);
      }
    } finally {
      setLoadingTickets(false);
    }
  };

  // Gestion du clic en dehors du sélecteur
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showStateSelector && !e.target.closest('.state-selector')) {
        setShowStateSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showStateSelector]);

  const getSurveillanceLabel = (state) => {
    const labels = {
      vert: "Normal",
      jaune: "Attention",
      orange: "Vigilance",
      rouge: "Critique"
    };
    return labels[state] || state;
  };

  const handleSubmit = async () => {
    if (!alertContent.trim()) {
      alert('Veuillez décrire le problème');
      return;
    }

    if (selectedState === 'vert') {
      alert('Un état "Normal" ne nécessite pas de ticket. Utilisez jaune, orange ou rouge pour signaler un problème.');
      return;
    }

    setSaving(true);
    try {
      const now = new Date().toISOString();
      
      const newTicket = {
        horseId: horse.id,
        horseName: horse.nom || 'Sans nom',
        horseSire: horse.sire || null,
        message: alertContent.trim(),
        etat: selectedState,
        dateCreation: now,
        dateModification: now,
        acteur: 'Utilisateur',
        statut: 'ouvert',
        historique: [{
          date: now,
          acteur: 'Utilisateur',
          message: alertContent.trim(),
          etat: selectedState,
          action: 'creation'
        }]
      };
      
      await addDoc(collection(db, 'tickets'), newTicket);
      
      const horseRef = doc(db, 'animaux', horse.id);
      const newAlert = {
        date: now,
        etat: selectedState,
        contenu: alertContent.trim(),
        createdAt: now
      };
      
      await updateDoc(horseRef, {
        SurveillanceEtat: selectedState,
        alertes: arrayUnion(newAlert),
        derniereAlerte: newAlert,
        derniereModification: now
      });

      if (onSuccess) {
        onSuccess();
      }

      onClose();
      alert(`Problème signalé pour ${horse.nom || 'le cheval'}`);
      
    } catch (error) {
      console.error('Erreur lors du signalement:', error);
      
      if (error.message && error.message.includes('arrayUnion')) {
        try {
          const now = new Date().toISOString();
          const horseRef = doc(db, 'animaux', horse.id);
          const newAlert = {
            date: now,
            etat: selectedState,
            contenu: alertContent.trim(),
            createdAt: now
          };
          
          await updateDoc(horseRef, {
            SurveillanceEtat: selectedState,
            alertes: [newAlert],
            derniereAlerte: newAlert,
            derniereModification: now
          });
          
          const newTicket = {
            horseId: horse.id,
            horseName: horse.nom || 'Sans nom',
            horseSire: horse.sire || null,
            message: alertContent.trim(),
            etat: selectedState,
            dateCreation: now,
            dateModification: now,
            acteur: 'Utilisateur',
            statut: 'ouvert',
            historique: [{
              date: now,
              acteur: 'Utilisateur',
              message: alertContent.trim(),
              etat: selectedState,
              action: 'creation'
            }]
          };
          
          await addDoc(collection(db, 'tickets'), newTicket);
          
          if (onSuccess) {
            onSuccess();
          }
          onClose();
          alert(`Problème signalé pour ${horse.nom || 'le cheval'}`);
          
        } catch (retryError) {
          console.error('Erreur lors de la création:', retryError);
          alert('Erreur lors de l\'enregistrement. Vérifiez que le cheval existe dans la base de données.');
        }
      } else {
        alert('Erreur lors de l\'enregistrement. Veuillez réessayer.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCloseTicket = async (ticketId) => {
    if (!confirm('Voulez-vous vraiment clôturer ce ticket ?')) return;
    
    try {
      const ticketRef = doc(db, 'tickets', ticketId);
      const now = new Date().toISOString();
      
      await updateDoc(ticketRef, {
        statut: 'cloture',
        dateCloture: now,
        historique: arrayUnion({
          date: now,
          acteur: 'Utilisateur',
          action: 'cloture'
        })
      });
      
      if (viewingTicket && viewingTicket.id === ticketId) {
        setViewingTicket(null);
      }
      
      loadOpenTickets();
      alert('Ticket clôturé');
    } catch (error) {
      console.error('Erreur clôture ticket:', error);
      alert('Erreur lors de la clôture du ticket');
    }
  };

  const handleViewTicket = async (ticket) => {
    setViewingTicket(ticket);
    
    try {
      const ticketRef = doc(db, 'tickets', ticket.id);
      const now = new Date().toISOString();
      
      await updateDoc(ticketRef, {
        historique: arrayUnion({
          date: now,
          acteur: 'Utilisateur',
          action: 'consultation'
        })
      });
    } catch (error) {
      console.error('Erreur enregistrement consultation:', error);
    }
  };

  const handleCloseView = () => {
    setViewingTicket(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', { 
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateMessage = (message, maxLength = 50) => {
    if (!message) return '';
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  if (!horse) return null;

  // Vue consultation d'un ticket
  if (viewingTicket) {
    return (
      <SimpleModal
        isOpen={isOpen}
        onClose={onClose}
        title={
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-600" />
            Consultation du ticket
          </div>
        }
      >
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: surveillanceColors[viewingTicket.etat] }}
                />
                <span className="font-semibold">
                  État: {getSurveillanceLabel(viewingTicket.etat)}
                </span>
              </div>
              <span className="text-sm text-gray-500">
                Ticket #{viewingTicket.id.slice(-6).toUpperCase()}
              </span>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">Créé le:</span>
                <span className="font-medium">{formatDate(viewingTicket.dateCreation)}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">Par:</span>
                <span className="font-medium">{viewingTicket.acteur}</span>
              </div>
              <div className="flex items-center gap-2">
                <GiHorseHead className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">Cheval:</span>
                <span className="font-medium">{viewingTicket.horseName}</span>
                {viewingTicket.horseSire && (
                  <span className="text-xs text-gray-500">(SIRE: {viewingTicket.horseSire})</span>
                )}
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">Description du problème:</h4>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-gray-700 whitespace-pre-wrap">{viewingTicket.message}</p>
            </div>
          </div>

          {viewingTicket.historique && viewingTicket.historique.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Historique:</h4>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-32 overflow-y-auto">
                <div className="space-y-1">
                  {viewingTicket.historique.map((entry, idx) => (
                    <div key={idx} className="text-xs text-gray-600 flex items-center gap-2">
                      <span className="font-medium">{formatDate(entry.date)}</span>
                      <span>-</span>
                      <span>{entry.acteur}</span>
                      <span>-</span>
                      <span className="italic">
                        {entry.action === 'creation' && 'Création du ticket'}
                        {entry.action === 'consultation' && 'Consultation'}
                        {entry.action === 'cloture' && 'Clôture'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={handleCloseView}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Fermer
            </button>
            <button
              onClick={() => handleCloseTicket(viewingTicket.id)}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              Clôturer le ticket
            </button>
          </div>
        </div>
      </SimpleModal>
    );
  }

  // Vue normale création/liste
  return (
    <SimpleModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-orange-600" />
          Signaler un problème
        </div>
      }
    >
      <div className="space-y-6">
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <GiHorseHead className="h-8 w-8 text-orange-600" />
              <div>
                <p className="font-semibold text-lg">{horse.nom || 'Sans nom'}</p>
                <p className="text-sm text-gray-600">
                  {horse.sire && <span>SIRE: {horse.sire}</span>}
                </p>
              </div>
            </div>
            {horse.SurveillanceEtat && (
              <div className="text-right">
                <p className="text-xs text-gray-500 mb-1">État actuel:</p>
                <div 
                  className="px-2 py-1 rounded-full text-white text-xs font-medium"
                  style={{ backgroundColor: surveillanceColors[horse.SurveillanceEtat] }}
                >
                  {getSurveillanceLabel(horse.SurveillanceEtat)}
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Niveau d'urgence
          </label>
          <div className="relative state-selector">
            <button
              onClick={() => setShowStateSelector(!showStateSelector)}
              className="w-full px-4 py-2 rounded-full text-white font-medium hover:opacity-90 transition-opacity cursor-pointer flex items-center justify-center"
              style={{ backgroundColor: surveillanceColors[selectedState] }}
              title="Cliquer pour changer le niveau d'urgence"
            >
              {getSurveillanceLabel(selectedState)}
            </button>
            
            {showStateSelector && (
              <div className="absolute top-full mt-2 left-0 right-0 flex justify-around gap-2 bg-white rounded-lg shadow-lg border p-3 z-50">
                <button
                  onClick={() => {
                    setSelectedState('vert');
                    setShowStateSelector(false);
                  }}
                  className={`flex-1 px-3 py-1 rounded-full text-white text-sm font-medium transition-all hover:scale-105 ${
                    selectedState === 'vert' ? 'ring-2 ring-gray-700 ring-offset-1' : ''
                  }`}
                  style={{ backgroundColor: surveillanceColors.vert }}
                >
                  Normal
                </button>
                
                <button
                  onClick={() => {
                    setSelectedState('jaune');
                    setShowStateSelector(false);
                  }}
                  className={`flex-1 px-3 py-1 rounded-full text-white text-sm font-medium transition-all hover:scale-105 ${
                    selectedState === 'jaune' ? 'ring-2 ring-gray-700 ring-offset-1' : ''
                  }`}
                  style={{ backgroundColor: surveillanceColors.jaune }}
                >
                  Attention
                </button>
                
                <button
                  onClick={() => {
                    setSelectedState('orange');
                    setShowStateSelector(false);
                  }}
                  className={`flex-1 px-3 py-1 rounded-full text-white text-sm font-medium transition-all hover:scale-105 ${
                    selectedState === 'orange' ? 'ring-2 ring-gray-700 ring-offset-1' : ''
                  }`}
                  style={{ backgroundColor: surveillanceColors.orange }}
                >
                  Vigilance
                </button>
                
                <button
                  onClick={() => {
                    setSelectedState('rouge');
                    setShowStateSelector(false);
                  }}
                  className={`flex-1 px-3 py-1 rounded-full text-white text-sm font-medium transition-all hover:scale-105 ${
                    selectedState === 'rouge' ? 'ring-2 ring-gray-700 ring-offset-1' : ''
                  }`}
                  style={{ backgroundColor: surveillanceColors.rouge }}
                >
                  Critique
                </button>
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description du problème
          </label>
          <textarea
            value={alertContent}
            onChange={(e) => setAlertContent(e.target.value)}
            rows={4}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            placeholder="Décrivez le problème observé..."
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Cette alerte sera enregistrée dans un ticket et dans la fiche du cheval.
          </p>
        </div>

        <div 
          className="p-3 rounded-lg border"
          style={{
            backgroundColor: surveillanceColors[selectedState] + '10',
            borderColor: surveillanceColors[selectedState]
          }}
        >
          <p className="text-sm" style={{ color: surveillanceColors[selectedState] }}>
            {selectedState === 'rouge' && "⚠️ Niveau critique : Intervention urgente requise"}
            {selectedState === 'orange' && "⚠️ Vigilance accrue : Surveillance rapprochée nécessaire"}
            {selectedState === 'jaune' && "⚠️ Attention : À surveiller régulièrement"}
            {selectedState === 'vert' && "✓ Normal : Simple note d'observation (pas de ticket créé)"}
          </p>
        </div>

        {openTickets.length > 0 && (
          <div className="border-t pt-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Tickets en cours ({openTickets.length})
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {openTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleViewTicket(ticket)}
                >
                  <div 
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: surveillanceColors[ticket.etat] }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Clock className="h-3 w-3" />
                      <span>{formatDate(ticket.dateCreation)}</span>
                      <User className="h-3 w-3 ml-2" />
                      <span>{ticket.acteur}</span>
                    </div>
                    <p className="text-sm text-gray-700 truncate">
                      {truncateMessage(ticket.message)}
                    </p>
                  </div>
                  <span className="text-xs text-blue-600">
                    Consulter →
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={saving}
          >
            Fermer
          </button>
          <button 
            onClick={handleSubmit}
            disabled={saving || !alertContent.trim()}
            className={`px-4 py-2 rounded-md flex items-center gap-2 ${
              saving || !alertContent.trim()
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-orange-600 text-white hover:bg-orange-700'
            }`}
          >
            {saving ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Créer le ticket
              </>
            )}
          </button>
        </div>
      </div>
    </SimpleModal>
  );
};

export default ReportProblemModal;